// Client-side environment configuration
// This file only uses import.meta.env for browser compatibility

export interface ClientEnvironmentConfig {
  // Firebase
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };

  // Supabase
  supabase: {
    url: string;
    anonKey: string;
  };

  // Cloudinary
  cloudinary: {
    cloudName: string;
    apiKey: string;
  };

  // App Configuration
  app: {
    url: string;
    name: string;
    mode: 'development' | 'production' | 'test';
    apiUrl: string;
  };

  // Optional client services
  stripe?: {
    publicKey?: string;
  };

  // Resend (for client-side email functionality)
  resend?: {
    apiKey?: string;
    fromEmail: string;
  };
}

// Check if we're in a browser/Vite environment
const isBrowser = typeof import.meta !== 'undefined' && import.meta.env;

/**
 * Get the app URL automatically based on runtime environment
 * In browser: uses window.location.origin
 * In Node.js: uses localhost with environment-specified port
 */
function getAppUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for server-side
  return process.env.VITE_APP_URL || 'http://localhost:3000';
}

/**
 * Get the API URL automatically
 * - Uses VITE_API_URL env var if set
 * - Otherwise derives from app URL (replaces port with 3002 in dev)
 * - Falls back to localhost:3002
 */
function getApiUrl(appUrl: string): string {
  // Check env var first
  if (isBrowser && import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  if (process.env.VITE_API_URL) {
    return process.env.VITE_API_URL;
  }

  // Derive from app URL in development
  try {
    const url = new URL(appUrl);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      // In dev, API is on port 3002
      url.port = '3002';
      return url.toString();
    }
  } catch {
    // Invalid URL, use fallback
  }

  return 'http://localhost:3002';
}

// Validate environment variables (only in browser)
const validateClientEnv = (): ClientEnvironmentConfig => {
  if (!isBrowser) {
    // Return default config for Node.js server side
    return {
      firebase: {
        apiKey: '',
        authDomain: '',
        projectId: '',
        storageBucket: '',
        messagingSenderId: '',
        appId: ''
      },
      supabase: {
        url: '',
        anonKey: ''
      },
      cloudinary: {
        cloudName: '',
        apiKey: ''
      },
      app: {
        url: 'http://localhost:3000',
        name: 'SwaySuite',
        mode: 'development',
        apiUrl: 'http://localhost:3002'
      }
    };
  }

  const requiredVars = {
    'VITE_FIREBASE_API_KEY': 'Firebase API Key',
    'VITE_FIREBASE_PROJECT_ID': 'Firebase Project ID',
    'VITE_FIREBASE_APP_ID': 'Firebase App ID',
    'VITE_SUPABASE_URL': 'Supabase URL',
    'VITE_SUPABASE_ANON_KEY': 'Supabase Anonymous Key',
    'VITE_CLOUDINARY_CLOUD_NAME': 'Cloudinary Cloud Name',
    'VITE_APP_NAME': 'App Name'
  };

  const missingVars: string[] = [];

  for (const [key, name] of Object.entries(requiredVars)) {
    if (!import.meta.env[key]) {
      missingVars.push(name);
    }
  }

  if (missingVars.length > 0) {
    throw new Error(
      `❌ Required environment variables not configured: ${missingVars.join(', ')}\n` +
      'This application requires all services to be configured. No mock data fallbacks are available.\n' +
      'Please configure all required environment variables in your .env file.'
    );
  }

  // Determine environment mode
  const mode = (import.meta.env.MODE || 'development') as ClientEnvironmentConfig['app']['mode'];

  // Get URLs automatically
  const appUrl = getAppUrl();
  const apiUrl = getApiUrl(appUrl);

  return {
    firebase: {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    },

    supabase: {
      url: import.meta.env.VITE_SUPABASE_URL || '',
      anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || ''
    },

    cloudinary: {
      cloudName: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME || '',
      apiKey: import.meta.env.VITE_CLOUDINARY_API_KEY || ''
    },

    app: {
      url: appUrl,
      name: import.meta.env.VITE_APP_NAME || 'SwaySuite',
      mode,
      apiUrl
    },

    // Optional services
    stripe: {
      publicKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY
    },

    resend: {
      apiKey: import.meta.env.VITE_RESEND_API_KEY,
      fromEmail: import.meta.env.VITE_RESEND_FROM_EMAIL || 'noreply@swaysuite.com'
    }
  };
};

// Export the validated environment configuration
export const clientConfig = validateClientEnv();

// Helper to check if we're in development
export const isDev = clientConfig.app.mode === 'development';

// Helper to check if we're in production
export const isProd = clientConfig.app.mode === 'production';

// Helper to check if a service is properly configured
export const isClientServiceConfigured = (service: keyof Omit<ClientEnvironmentConfig, 'app'>): boolean => {
  const serviceConfig = clientConfig[service];

  switch (service) {
    case 'firebase':
      const firebaseConfig = serviceConfig as ClientEnvironmentConfig['firebase'];
      return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);
    case 'supabase':
      const supabaseConfig = serviceConfig as ClientEnvironmentConfig['supabase'];
      return !!(supabaseConfig.url && supabaseConfig.anonKey);
    case 'cloudinary':
      const cloudinaryConfig = serviceConfig as ClientEnvironmentConfig['cloudinary'];
      return !!(cloudinaryConfig.cloudName && cloudinaryConfig.apiKey);
    default:
      return true;
  }
};

// Log configuration status (only in development)
if (isDev) {
  console.log(
    '🔧 Client Environment Configuration:\n' +
    `  - Mode: ${clientConfig.app.mode}\n` +
    `  - Firebase: ${isClientServiceConfigured('firebase') ? '✅ Configured' : '❌ Missing vars'}\n` +
    `  - Supabase: ${isClientServiceConfigured('supabase') ? '✅ Configured' : '❌ Missing vars'}\n` +
    `  - Cloudinary: ${isClientServiceConfigured('cloudinary') ? '✅ Configured' : '❌ Missing vars'}\n` +
    `  - App URL: ${clientConfig.app.url}\n` +
    `  - API URL: ${clientConfig.app.apiUrl}`
  );
}